import { levelFitting } from './zoom';

export interface Camera {
  zoom: number;
  pan: { x: number; y: number };
}

export interface Box {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

/**
 * Wohin die Kamera gleiten muss, damit `box` (Modellraum) im Bild ist: nichts (null), wenn sie es schon ist. Sonst wird der Bereich
 * mittig gezeigt; der Zoom ändert sich nur, wenn der Bereich nicht hineinpasst, und dann nur heraus auf eine der festen Stufen.
 * Passt er in einer Richtung schon hinein, bleibt die Kamera in dieser Richtung, wo sie ist (keine unnötige Bewegung).
 */
export function focusCamera(view: { w: number; h: number }, cam: Camera, box: Box, margin = { x: 90, y: 70 }): Camera | null {
  const { zoom, pan } = cam;
  const inside = (z: number, p: { x: number; y: number }, axis: 'x' | 'y'): boolean => {
    const [a, b, size, m] = axis === 'x' ? [box.x1, box.x2, view.w, margin.x] : [box.y1, box.y2, view.h, margin.y];
    return a * z + p[axis] >= m && b * z + p[axis] <= size - m;
  };
  if (inside(zoom, pan, 'x') && inside(zoom, pan, 'y')) return null;
  const need = Math.min((view.w - 2 * margin.x) / Math.max(box.x2 - box.x1, 1), (view.h - 2 * margin.y) / Math.max(box.y2 - box.y1, 1));
  const next = zoom <= need + 1e-9 ? zoom : Math.min(zoom, levelFitting(need));
  const center = { x: view.w / 2 - ((box.x1 + box.x2) / 2) * next, y: view.h / 2 - ((box.y1 + box.y2) / 2) * next };
  const same = next === zoom;
  return {
    zoom: next,
    pan: { x: same && inside(zoom, pan, 'x') ? pan.x : center.x, y: same && inside(zoom, pan, 'y') ? pan.y : center.y },
  };
}

/** Weiches Beschleunigen und Abbremsen (kubisch). */
export const easeInOutCubic = (t: number): number => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);

/** Rand, den eine Szene beim Rahmen zum Fensterrand hält (Bildschirm) */
const FRAME_MARGIN = { x: 110, y: 90 };

/**
 * Kamera, die eine Szene in Lesegröße rahmt (PF 11): `around` (die Szene mit ihren Nachbarn) ganz und mittig, auf der größten festen
 * Zoomstufe bis `max`, bei der es hineinpasst. Passt es erst unter `min`, steht die Szene selbst (`scene`) mittig auf `min` – die
 * Nachbarn sind dann nur teils zu sehen.
 */
export function frameCamera(view: { w: number; h: number }, scene: Box, around: Box, { min = 0.65, max = 0.95, margin = FRAME_MARGIN } = {}): Camera {
  const fit = (b: Box): number => Math.min((view.w - 2 * margin.x) / Math.max(b.x2 - b.x1, 1), (view.h - 2 * margin.y) / Math.max(b.y2 - b.y1, 1));
  const all = levelFitting(fit(around));
  const [zoom, box] = all >= min ? [Math.min(all, max), around] : [min, scene];
  return { zoom, pan: { x: view.w / 2 - ((box.x1 + box.x2) / 2) * zoom, y: view.h / 2 - ((box.y1 + box.y2) / 2) * zoom } };
}

/**
 * Kamera nach einem Klick auf die Fläche selbst: Sie bewegt sich nur, wenn die Szene mit ihren Nachbarn nicht ganz zu sehen ist und bei
 * diesem Zoom hineinpasst – dann so wenig wie möglich, ohne zu zoomen. Sonst bleibt sie (null), damit beim Arbeiten nichts springt.
 */
export function nearCamera(view: { w: number; h: number }, cam: Camera, around: Box, margin = { x: 90, y: 70 }): Camera | null {
  const fits = (around.x2 - around.x1) * cam.zoom <= view.w - 2 * margin.x && (around.y2 - around.y1) * cam.zoom <= view.h - 2 * margin.y;
  return fits ? focusCamera(view, cam, around, margin) : null;
}
